/**
 * PerfilPage — dados do user + trocar senha (B1.8 §8 — todos perfis).
 *
 * Mostra:
 *   - Dados do user (nome, email)
 *   - Clientes vinculados (com role)
 *   - Form trocar senha (senha_atual + nova + confirmar)
 *
 * Backend:
 *   - GET /api/v1/cliente/perfil
 *   - PUT /api/v1/cliente/perfil/senha
 *
 * Disponível pra QUALQUER perfil (Gestor + Portaria). Sem feature_key check.
 *
 * NÃO REVERTER:
 *   - Senha atual sempre exigida (backend bcrypt compare)
 *   - Nova senha passa por validatePasswordPolicy (mín 8 chars + complexidade)
 *   - Form local não persiste valores em state global (segurança)
 */

import { useState } from 'react';

import { Alert, Badge, Button, Input, Label } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { usePerfil, useTrocarSenha } from '@/lib/cliente-portal-store';
import { ClienteApiError } from '@/lib/cliente-api';
import { mensagemErro } from '@/lib/mensagem-publica';

export function PerfilPage() {
  const { data, isLoading, isError, error } = usePerfil();
  const trocarSenha = useTrocarSenha();
  const toast = useToast();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleTrocarSenha(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (novaSenha.length < 8) {
      setErrorMsg('A nova senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErrorMsg('As duas senhas digitadas não são iguais.');
      return;
    }
    try {
      await trocarSenha.mutateAsync({
        senha_atual: senhaAtual,
        nova_senha: novaSenha,
      });
      toast.show('Senha alterada com sucesso.', { variant: 'success' });
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
    } catch (err) {
      if (err instanceof ClienteApiError) {
        if (err.status === 401) {
          setErrorMsg('Senha atual incorreta.');
          return;
        }
        if (err.status === 400 && /senha-fraca|policy/i.test(err.message)) {
          setErrorMsg('Essa senha é fácil demais. Use ao menos 8 caracteres com letras e números.');
          return;
        }
      }
      setErrorMsg(mensagemErro(err, 'Não conseguimos trocar sua senha agora. Tente de novo.'));
    }
  }

  return (
    <PortalPage
      testId="perfil-page"
      title="Meu perfil"
      subtitle="Seus dados de acesso ao Portal IGM Security."
    >
      {isLoading ? (
        <PortalCarregando linhas={2} rotulo="Carregando seu perfil…" />
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(error, 'Não conseguimos carregar seu perfil agora. Tente atualizar a página.')}
        </Alert>
      ) : !data ? null : (
        <>
          {/* Dados */}
          <section className="mb-6 rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Seus dados
            </h2>
            <dl className="space-y-2 text-sm">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="w-24 text-ink-3 dark:text-ink-dark-3">Nome</dt>
                <dd className="font-medium text-ink-1 dark:text-ink-dark-1">{data.user.nome}</dd>
              </div>
              <div className="flex flex-wrap items-baseline gap-x-2">
                <dt className="w-24 text-ink-3 dark:text-ink-dark-3">E-mail</dt>
                <dd className="break-all font-medium text-ink-1 dark:text-ink-dark-1">
                  {data.user.email}
                </dd>
              </div>
            </dl>
            <p className="mt-3 text-xs text-ink-2 dark:text-ink-dark-2">
              Para mudar nome ou e-mail, fale com a IGM.
            </p>
          </section>

          {/* Condomínios vinculados */}
          <section className="mb-6 rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Onde você tem acesso ({data.clientes.length})
            </h2>
            {data.clientes.length === 0 ? (
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                Sua conta ainda não está vinculada a nenhum condomínio. Fale com a IGM.
              </p>
            ) : (
              <ul className="space-y-2">
                {data.clientes.map((c) => (
                  <li
                    key={c.clientId}
                    className="flex items-center justify-between gap-3 text-sm"
                    data-testid={`perfil-cliente-${c.clientId}`}
                  >
                    <span className="min-w-0 truncate text-ink-1 dark:text-ink-dark-1">
                      {c.clienteNome}
                    </span>
                    <Badge variant={c.role === 'gestor' ? 'warning' : 'info'} size="sm">
                      {c.role === 'gestor' ? 'Gestor' : 'Portaria'}
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Trocar senha */}
          <section
            className="rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface"
            data-testid="perfil-trocar-senha"
          >
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Trocar senha
            </h2>
            <form onSubmit={handleTrocarSenha} className="space-y-3">
              <div>
                <Label htmlFor="senha-atual" required>
                  Senha atual
                </Label>
                <Input
                  id="senha-atual"
                  type="password"
                  value={senhaAtual}
                  onChange={(e) => setSenhaAtual(e.target.value)}
                  autoComplete="current-password"
                  disabled={trocarSenha.isPending}
                  required
                  data-testid="perfil-senha-atual"
                />
              </div>
              <div>
                <Label htmlFor="nova-senha" required>
                  Nova senha
                </Label>
                <Input
                  id="nova-senha"
                  type="password"
                  value={novaSenha}
                  onChange={(e) => setNovaSenha(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  disabled={trocarSenha.isPending}
                  required
                  data-testid="perfil-nova-senha"
                />
                <p className="mt-1 text-xs text-ink-2 dark:text-ink-dark-2">
                  Pelo menos 8 caracteres, com letras e números.
                </p>
              </div>
              <div>
                <Label htmlFor="confirmar-senha" required>
                  Repita a nova senha
                </Label>
                <Input
                  id="confirmar-senha"
                  type="password"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  autoComplete="new-password"
                  minLength={8}
                  disabled={trocarSenha.isPending}
                  required
                  data-testid="perfil-confirmar-senha"
                />
              </div>
              {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full sm:w-auto"
                  disabled={trocarSenha.isPending || !senhaAtual || !novaSenha || !confirmarSenha}
                  data-testid="perfil-trocar-senha-submit"
                >
                  {trocarSenha.isPending ? 'Salvando…' : 'Trocar senha'}
                </Button>
              </div>
            </form>
          </section>
        </>
      )}
    </PortalPage>
  );
}
